import {
  AssessmentPeriodStatusOption,
  ContributionLevelOption,
  EvaluationStatusOption,
  FeedBackStatusOption,
  PeerEvaluationStatusOption,
  RewardPeriodStatusOption,
  RewardRequestOption,
  WelcomeProject
} from '@/models';

const CHARACTERISTICS = {
  ACCOUNTABILITY: {
    name: 'Accountability',
    tooltip: "How well did this member take responsibility for their tasks as well as the team's performance?"
  },
  COMMUNICATION: {
    name: 'Communication',
    tooltip: 'How well did this member communicate to you and other team members throughout the project?'
  },
  SUPPORTIVENESS: {
    name: 'Supportiveness',
    tooltip: 'How well did this member support you and other team members throughout the project?'
  },
  PERSONAL_ATTRIBUTES: {
    name: 'Personal Attributes',
    tooltip: 'How pleasant did you feel while working with this member?'
  },
  PROFESSIONAL_CONDUCT: {
    name: 'Professional Conduct',
    tooltip: 'How professional did this member behave throughout the project?'
  }
};

const CONFETTI_COLORS = ['#FEC502', '#6AD4EE', '#C851A0'];

const DATE_FORMAT = {
  DISPLAY: 'MMM DD, YYYY',
  STORED: 'YYYY-MM-DDTHH:mm:ss',
  SHORT: 'DD/MM/YYYY',
  MONTH_YEAR: 'MMM YYYY'
};

const DEFAULT_DISPLAY_VALUE = {
  DATE: '-'
};

const EMOTIONS = {
  WORST: 'worst',
  POOR: 'poor',
  OKAY: 'okay',
  SO_SO: 'so-so',
  SUPERB: 'superb'
};

//#region statuses
// TODO: Check all of STATUSES constants
const STATUS_TYPES = {
  ASSESSMENT_PERIOD: 'ASSESSMENT_PERIOD',
  FEEDBACK: 'FEEDBACK',
  EVALUATION: 'EVALUATION',
  PROJECT_ASSESSMENT: 'PROJECT_ASSESSMENT',
  PROJECT_PEER_EVALUATION: 'PROJECT_PEER_EVALUATION',
  REWARD_PERIOD: 'REWARD_PERIOD',
  REWARD_REQUEST: 'REWARD_REQUEST',
  PEER_EVALUATION: 'PEER_EVALUATION'
};
const STATUS_COLORS = {
  NOT_STARTED: '#C4C4C4',
  ON_GOING: '#FEC400',
  STARTED: '#FEC400',
  DONE: '#8CC63F',
  REJECTED: '#F44336',
  IN_PROGRESS: '#FFCB45'
};

const ASSESSMENT_PERIOD_STATUSES: AssessmentPeriodStatusOption = {
  ON_GOING: {
    color: STATUS_COLORS.ON_GOING,
    label: 'ON_GOING'
  },
  DONE: {
    color: STATUS_COLORS.DONE,
    label: 'DONE'
  }
};

const EVALUATION_STATUSES: EvaluationStatusOption = {
  NOT_STARTED: {
    color: STATUS_COLORS.NOT_STARTED,
    label: 'NOT_STARTED',
    sortValue: 0
  },
  ON_GOING: {
    color: STATUS_COLORS.ON_GOING,
    label: 'ON_GOING',
    sortValue: 2
  },
  DONE: {
    color: STATUS_COLORS.DONE,
    label: 'DONE',
    sortValue: 1
  }
};

const REWARD_PERIOD_STATUSES: RewardPeriodStatusOption = {
  NOT_STARTED: {
    color: STATUS_COLORS.NOT_STARTED,
    label: 'NOT_STARTED'
  },
  PUBLISHING: {
    color: STATUS_COLORS.ON_GOING,
    label: 'PUBLISHING'
  },
  CLOSED: {
    color: STATUS_COLORS.DONE,
    label: 'CLOSED'
  }
};

const REWARD_REQUEST_STATUSES: RewardRequestOption = {
  PENDING: {
    color: STATUS_COLORS.NOT_STARTED,
    label: 'PENDING'
  },
  IN_PROGRESS: {
    color: STATUS_COLORS.IN_PROGRESS,
    label: 'IN_PROGRESS'
  },
  REJECTED: {
    color: STATUS_COLORS.REJECTED,
    label: 'REJECTED'
  },
  USER_APPROVED: {
    color: STATUS_COLORS.DONE,
    label: 'USER_APPROVED'
  },
  APPROVED: {
    color: STATUS_COLORS.DONE,
    label: 'APPROVED'
  },
  APPROVED_WITH_REFUND: {
    color: STATUS_COLORS.DONE,
    label: 'APPROVED_WITH_REFUND'
  }
};

const REWARD_DROP = {
  VALUE_RANGE: [
    100, 200, 300, 400, 500, 1000, 1500, 2000, 2500, 3000, 3500, 4000, 4500, 5000, 5500, 6000, 6500, 7000, 7500, 8000,
    8500, 9000, 9500, 10000
  ],
  RATIO: [
    1.55, 1.5, 1.45, 1.4, 1.35, 1.3, 1.25, 1.2, 1.15, 1.1, 1.05, 1.0, 0.99, 0.98, 0.97, 0.96, 0.95, 0.94, 0.93, 0.92,
    0.91, 0.9, 0.89, 0.88
  ]
};

const PROJECT_ASSESSMENT_STATUSES = {
  PROJECT_CREATION: 'PROJECT_CREATION',
  MEMBER_CONTRIBUTION_EVALUATION: 'MEMBER_CONTRIBUTION_EVALUATION',
  PROJECT_EVALUATION: 'PROJECT_EVALUATION',
  PEER_EVALUATION: 'PEER_EVALUATION',
  RESULT_RELEASE: 'RESULT_RELEASE'
};

const PROJECT_PEER_EVALUATION_STATUSES: PeerEvaluationStatusOption = {
  STARTED: {
    color: STATUS_COLORS.STARTED,
    label: 'STARTED'
  },
  DONE: {
    color: STATUS_COLORS.DONE,
    label: 'DONE'
  }
};
//#endregion

const HIVE_HOME_URL = process.env.VUE_APP_HIVE_HOME_URL as string;
const SSO_URL = process.env.VUE_APP_SSO_URL as string;
const SSO_CLIENT_ID = process.env.VUE_APP_SSO_CLIENT_ID as string;
const HRM_LINK = process.env.VUE_APP_HRM_LINK as string;

const CONTRIBUTION_LEVELS = {
  EGG: {
    name: 'Egg',
    min: 1,
    max: 49,
    iconClass: 'contribution-level-icon__egg'
  },
  LARVA: {
    name: 'Larva',
    min: 50,
    max: 99,
    iconClass: 'contribution-level-icon__larva'
  },
  PUPA: {
    name: 'Pupa',
    min: 100,
    max: 179,
    iconClass: 'contribution-level-icon__pupa'
  },
  BEE: {
    name: 'Bee',
    min: 180,
    max: 379,
    iconClass: 'contribution-level-icon__worker'
  },
  SUPER_BEE: {
    name: 'Super Bee',
    min: 380,
    max: 479,
    iconClass: 'contribution-level-icon__warrior'
  },
  MASTER_BEE: {
    name: 'Master Bee',
    min: 480,
    max: Number.MAX_SAFE_INTEGER,
    iconClass: 'contribution-level-icon__king'
  }
} as ContributionLevelOption;

const MENU_ACTIONS = {
  DELETE: 'DELETE',
  EXPORT: 'EXPORT',
  STOP_EVALUATION: 'STOP EVALUATION',
  CLOSE_PERIOD: 'CLOSE PERIOD',
  IMPORT_MEMBERS: 'IMPORT MEMBERS',
  CLEAR_ALL: 'CLEAR ALL',
  PUBLISH: 'PUBLISH',
  LINK_HRM_PROJECT: 'EDIT HRM INTEGRATION',
  ARCHIVE: 'ARCHIVE',
  UNARCHIVE: 'UNARCHIVE',
  EDIT_MANAGER: 'EDIT MANAGER'
};

const PAGE_NAMES = {
  HOME: 'Home',
  LOGIN: 'Login',
  INITIATE_EVALUATION: 'Initiate Evaluation',
  PROFILE: 'Profile',
  PROJECT_LIST: 'Projects',
  PROJECT_CREATION: 'Project Creation',
  PROJECT_EVALUATION: 'Project Evaluation',
  PROJECT_EXPORT: 'Project Export',
  NOTIFICATION: 'Notification',
  ADJUST_INDIVIDUAL_DROPS: 'Adjust Individual Pollens',
  USER_EVALUATION_DETAILS: 'User Evaluation Details',
  ASSESSMENT_PERIODS: 'Periods',
  ASSESSMENT_PERIOD_DETAIL: 'Assessment Period Detail',
  CONTRIBUTION_LEVELS: 'Levels',
  NOT_FOUND: 'NOT FOUND',
  MANAGEMENTS: 'Manage',
  ORGANIZATION: 'Organization',
  REWARDS: 'Rewards',
  REWARD_MANAGEMENT: 'Manage Rewards',
  REWARD_PERIOD_DETAIL: 'Reward Period Detail',
  WELCOME: 'Welcome'
};

const PROJECT_TYPES = {
  HOURLY_BASED: 'HOURLY_BASED',
  INDIVIDUAL_TRACKING: 'INDIVIDUAL_TRACKING',
  FIXED: 'FIXED'
};

const USER_ENGAGEMENT_ROLES = {
  PROJECT_MANAGER: 'PROJECT_MANAGER',
  PROJECT_MEMBER: 'PROJECT_MEMBER'
};

const PROJECT_ASSESSMENT_TOOLTIP = {
  SAT: 'SAT, or project satisfaction, is the level of the sponsor’s satisfaction with the current project.',
  COMPLEXITY: 'Complexity represents the Sponsor’s subjective evaluation of how complex this project is.',
  KEY: 'KEY, or project weight, is a number that represents the importance of the current project.',
  PLANNED_EFFORT:
    'Planned effort is the budgeted number of hours for this project, which will be determined by the project’s sponsor.',
  ACTUAL_EFFORT:
    'Actual effort is the actual number of hours spent on this project, which is reported by the project manager.',
  PROJECT_BASE_VALUE: 'Project base value = Planned Effort x KEY.',
  ADDED_VALUE:
    'Added value is evaluated based on the sponsor’s intuitive judgment on project satisfaction and complexity. For T&M projects, efficiency also affects Added value.'
};

const EVALUATION_DROP_TOOLTIP = {
  CONTRIBUTION_DROP: 'Contribution pollens is the total value you contribute to this project.',
  GIVING_DROP: 'The total number of pollens you distributed to other members of your team.',
  RECEIVED_DROP: 'The total number of pollens you were distributed by the other members of your team.',
  TOTAL_EARNED_DROP:
    'The total number of pollens you gain from this project. Individual pollens = Contribution pollens - Giving pollens + Received pollens.'
};

const EVALUATION_STEPS = {
  [PROJECT_ASSESSMENT_STATUSES.PROJECT_CREATION]: {
    key: PROJECT_ASSESSMENT_STATUSES.PROJECT_CREATION,
    name: 'Project Creation',
    role: 'by Sponsor'
  },
  [PROJECT_ASSESSMENT_STATUSES.MEMBER_CONTRIBUTION_EVALUATION]: {
    key: PROJECT_ASSESSMENT_STATUSES.MEMBER_CONTRIBUTION_EVALUATION,
    name: 'Member Contribution Evaluation',
    role: 'by Project Manager '
  },
  [PROJECT_ASSESSMENT_STATUSES.PROJECT_EVALUATION]: {
    key: PROJECT_ASSESSMENT_STATUSES.PROJECT_EVALUATION,
    name: 'Project Evaluation',
    role: 'by Sponsor'
  },
  [PROJECT_ASSESSMENT_STATUSES.PEER_EVALUATION]: {
    key: PROJECT_ASSESSMENT_STATUSES.PEER_EVALUATION,
    name: 'Peer Evaluation',
    role: 'by Project Members'
  },
  [PROJECT_ASSESSMENT_STATUSES.RESULT_RELEASE]: {
    key: PROJECT_ASSESSMENT_STATUSES.RESULT_RELEASE,
    name: 'Result Release',
    role: ''
  }
};

const APP_ROLES = {
  ADMIN: 'Admin',
  NORMAL: 'Normal',
  PROJECT_ADMIN: 'Project Admin'
};

const PROJECT_ROLES = {
  SPONSOR: 'Sponsor',
  MANAGER: 'Manager',
  MEMBER: 'Member'
};

const NOTIFICATION = {
  TYPE: {
    success: 'success',
    error: 'error',
    info: 'info'
  },
  ICON: {
    success: 'mdi-checkbox-marked-circle',
    error: 'mdi-alert-outline',
    info: 'mdi-information-outline',
    close: 'mdi-close'
  },
  TEXT: {
    approveSuccess: 'Approve successfully',
    confirmSuccess: 'Confirm successfully',
    confirmFailed: 'Confirm failed',

    saveMemberContributionEvaluationFailed: 'Save member contribution evaluation failed',
    saveMemberContributionEvaluationSuccess: 'Save member contribution evaluation successfully',
    confirmMemberContributionEvaluationFailed: 'Confirm member contribution evaluation failed',
    confirmMemberContributionEvaluationSuccess: 'Confirm member contribution evaluation successfully',

    saveProjectEvaluationFailed: 'Save project evaluation failed',
    saveProjectEvaluationSuccess: 'Save project evaluation successfully',
    confirmProjectEvaluationFailed: 'Confirm project evaluation failed',
    confirmProjectEvaluationSuccess: 'Confirm project evaluation successfully',

    savePeerEvaluationFailed: 'Save peer evaluation failed',
    savePeerEvaluationSuccess: 'Save peer evaluation successfully',
    confirmPeerEvaluationFailed: 'Confirm peer evaluation failed',
    confirmPeerEvaluationSuccess: 'Confirm peer evaluation successfully',
    initiateEvaluationSuccess: 'Successfully initiate evaluation',

    resolveFeedbackSuccess: 'Resolve feedback successfully',
    resolveFeedbackFailed: 'Resolve feedback failed',
    rejectFeedbackSuccess: 'Reject feedback successfully',
    rejectFeedbackFailed: 'Reject feedback failed',

    sendFeedbackSuccess: 'Send feedback successfully',
    sendFeedbackFailed: 'Send feedback failed',

    saveAdjustReceivedDropsFailed: 'Save adjust received pollens failed',

    importMemberFailed: 'Error while trying to import members to project',
    importMemberSuccess: 'Import members to projects successfully',
    dataHaveDifferentKey: 'Data have different keys',
    importCancel: 'Import of file canceled',
    differentKeyDialogTitle:
      'The KEY in your imported data is different from the existing data. Please choose only one KEY for this project.',

    confirmRedeemPrizeSuccess: 'Confirm redeem prize successfully',
    confirmRedeemPrizeFailed: 'Confirm redeem prize failed',
    cancelRewardRequestSuccess: 'Cancel reward request successfully',
    cancelRewardRequestFailed: 'Cancel reward request failed',

    addRewardItemSuccess: 'Add reward item successfully',
    addRewardItemFailed: 'Add reward item failed',
    editRewardItemSuccess: 'Edit reward item successfully',
    editRewardItemFailed: 'Edit reward item failed',
    removeRewardItemSuccess: 'Remove reward item successfully',
    removeRewardItemFailed: 'Remove reward item failed',
    voucherRedeemLimitReached: 'You have reached the maximum number of {formattedType} allowed in your gift basket. Please select another type of gift.',
    approveRewardRequestSuccess: 'Approve reward request successfully',
    approveRewardRequestFailed: 'Approve reward request failed',
    rejectRewardRequestSuccess: 'Reject reward request successfully',
    rejectRewardRequestFailed: 'Reject reward request failed',
    inProgressRewardRequestSuccess: 'Change gift request status to In Progress successfully',
    inProgressRewardRequestFailed: 'Change gift request status to In Progress failed',
    publishRewardPeriodSuccess: 'Publish reward period successfully',
    publishRewardPeriodFailed: 'Publish reward period failed',
    closeRewardPeriodSuccess: 'Close reward period successfully',
    closeRewardPeriodFailed: 'Close reward period failed',

    exportDataSuccess: 'Export data successfully',

    mustAddShippingInfo: 'You must add shipping info before redeem gift',
    updateShippingInfoSuccess: 'Update shipping info successfully',
    updateShippingInfoFailed: 'Update shipping info failed',

    updateManagersSuccess: 'Update managers successfully',
    updateManagersFailed: 'Update managers failed'
  }
};

const FEEDBACK_TYPES = {
  GIVE_FEEDBACK: 'GIVE_FEEDBACK',
  SUGGEST_A_FEATURE: 'SUGGEST_A_FEATURE',
  REPORT_A_BUG: 'REPORT_A_BUG'
};

const FEEDBACK_STATUSES: FeedBackStatusOption = {
  PENDING: {
    color: STATUS_COLORS.ON_GOING,
    label: 'PENDING'
  },
  RESOLVED: {
    color: STATUS_COLORS.DONE,
    label: 'RESOLVED'
  },
  REJECTED: {
    color: STATUS_COLORS.REJECTED,
    label: 'REJECTED'
  }
};

const WELCOME_PROJECT_LIST: { [key: string]: WelcomeProject } = {
  INTERVIEW: {
    title: 'Delivery Support',
    isUpTo: true,
    drops: 25,
    unit: 'hour',
    description:
      `<h3>Sales Support: </h3>
        <h6>
            Support Delivery team in developing proposals by attending discovery meetings, <br />
            conducting research, providing estimates, preparing/presenting slides, <br />
            and preparing Statements of Work (SOW) 
        </h6>
      <h3>iQBR Preparation & Conduct:</h3>
       <h6>Prepare for and conduct internal quarterly business reviews for other team,
        analyzing performance metrics and identifying areas for improvement</h6>
      <h3>GenAI Support:</h3> 
        <h6>[KMS Technology] 🐝 Bee-yond Program 🐝  Project List 100% E4 Get involved 
        in building initiative focused on applying Generative AI to software development. 
        Get involved in building initiative focused on applying Generative AI
        to software development. </h6>
      `,
      orgId: 1
  },
  REFERRAL: {
    title: 'Hiring Support',
    isUpTo: false,
    drops: 0,
    unit: 'time',
    description: `
          <h3>Interview Support:</h3> 
            <h6>Support for interviewing to recruit new resources for the company 
            (Hour tracked is the same interview hours which schedule on SmartRecruiters)
            </h6>
          <h3> Candidate Referral:</h3> 
          <h6> Refer a candidate's CV for open posit
          ions at KMS (The candidate's CV must not already be i
          n the KMS database within the last six months)</h6>
    `,
    orgId: 1
  },
  MENTOR: {
    title: 'Expertise Sharing Support',
    isUpTo: false,
    drops: 0,
    unit: 'time',
    description: `
    <h3>External Speaking:</h3> 
     <h6>Recognize KMSers who speak at technical conferences, events
     to share KMS expertise in artificial intelligence and technology</h6>
    <h3>AI Technical Writing:</h3> 
     <h6> 
      - Recognize KMSers who write personal content about AI technology: AI applications, AI trends, and AI productivity enhancement on LinkedIn to showcase KMS expertise in artificial intelligence</br>
      - Recognize contributors who review and qualify AI content posts on LinkedIn </br>
      - Support interviews and review technical content for the Humans of KMS series</h6>
    <h3>Internal Training/Sharing:</h3> 
    <h6> Conduct internal training or knowledge-sharing sessions within the team,
     account, program or department scope (excluding efforts already counted under the Lea
     rning & Development Contributor Program, ex: Guild activities)</h6>`,
    orgId: 1
  },
  KNOWLEDGE_SHARING: {
    title: 'External/Internal Event Support',
    isUpTo: true,
    drops: 100,
    unit: 'hour',
    orgId: 1,
    description: `
      <h3>L&D-Related Events Support:</h3> 
         <h6> Support for organizing and running KMS learning activities.
         </h6>
       <h3>Other Events Support:</h3> 
         <h6>Support for organizing and running other internal and external KMS events, including but not limited to: YEP Performance, 
         Office Tours, X-Mas Day, Mid-Autumn, Women's Day, ...</h6>`
  },
  SUPPORT: {
    title: 'Internal KMS Tool Support',
    isUpTo: false,
    drops: 0,
    unit: 'hour',
    orgId: 1,
    description: `
      <h3>Support for developing/testing/maintaining internal KMS tools:</h3> 
         <h6> 
          - Bee-yond </br>
          - Prism </br>
         </h6>
      <h3>Support for developing/testing/maintaining internal KMS tools:</h3> 
         <h6> 
          - Agilis - Task </br>
          - Agilis - Test </br>
          - Gravity </br>
          - Profile Hub </br>
          - CV Scanner </br>
         </h6>`
  },
};

const PRIZE_TYPES = {
  BACKPACK: 'BACKPACK',
  VOUCHER: 'VOUCHER',
  FOOD: 'FOOD',
  COMPUTER_DEVICE: 'COMPUTER_DEVICE',
  HOUSEWARE: 'HOUSEWARE',
  HEALTHCARE: 'HEALTHCARE',
  ELECTRONIC_DEVICE: 'ELECTRONIC_DEVICE',
  OTHER: 'OTHER'
};

const LOCAL_STORAGE_KEYS = {
  USER: 'hive.user',
  ON_BOARDING_VIEWED: 'hive.onBoardingViewed',
  LAST_ACCESS_LEVEL: 'hive.lastAccessLevel'
};

const TASK_TYPES = {
  CONFIRM_INDIVIDUAL_RESULTS: 'CONFIRM_INDIVIDUAL_RESULTS',
  HANDLE_PROJECT: 'HANDLE_PROJECT',
  HANDLE_FEEDBACK: 'HANDLE_FEEDBACK',
  HANDLE_REWARD_REQUEST: 'HANDLE_REWARD_REQUEST'
};

const HIVE_STATISTICS_TEXT = {
  evaluatedMembers: 'Members evaluated',
  projectHours: 'Total project hours',
  projects: 'Total projects',
  accumulatedDrops: 'Total accumulated pollens'
};

const USER_ENGAGEMENT_SOURCES = {
  HRM_DATA: 'HRM_DATA',
  HRM_DATA_EDITED: 'HRM_DATA_EDITED',
  HRM_DATA_ADD_NEW: 'HRM_DATA_ADD_NEW',
  IMPORTED_FROM_FILE: 'IMPORTED_FROM_FILE',
  MANUALLY_INPUT: 'MANUALLY_INPUT',
};

const IMPORTED_USER_DATA = '[System] Manually Input';

const USER_ENGAGEMENT_SOURCES_TEXT = {
  [USER_ENGAGEMENT_SOURCES.HRM_DATA]: 'HRM Data',
  [USER_ENGAGEMENT_SOURCES.HRM_DATA_EDITED]: 'HRM Data (Edited)',
  [USER_ENGAGEMENT_SOURCES.HRM_DATA_ADD_NEW]: 'HRM Data (Add New)',
  [USER_ENGAGEMENT_SOURCES.IMPORTED_FROM_FILE]: 'File Import',
  [USER_ENGAGEMENT_SOURCES.MANUALLY_INPUT]: 'Manually Input'
};

const INDIVIDUAL_POP_UP_MODE = {
  VIEW: 'VIEW',
  EDIT: 'EDIT',
  HIDE: 'HIDE',
  ADD: 'ADD'
};
const TIME_TO_DETECT_AUTOMATICALLY_ADDED_PROJECTS = 3000;
const LETTER_A_IN_ASCII_CODE = 65;
const ALPHABET_LETTER_NUMBERS = 26;

const HIVE_GUIDELINE_LINK = [
  {
    link: 'https://docs.google.com/presentation/d/17ANR2M6EhJP_dtpRmnatwiKTdNYR4m93tDicHoMi818/',
    org_id: 1
  }
];

export const ALL_TIME_VALUE = -1;

export {
  APP_ROLES,
  ALPHABET_LETTER_NUMBERS,
  ASSESSMENT_PERIOD_STATUSES,
  CHARACTERISTICS,
  CONFETTI_COLORS,
  CONTRIBUTION_LEVELS,
  DATE_FORMAT,
  DEFAULT_DISPLAY_VALUE,
  EMOTIONS,
  EVALUATION_DROP_TOOLTIP,
  EVALUATION_STATUSES,
  EVALUATION_STEPS,
  FEEDBACK_STATUSES,
  FEEDBACK_TYPES,
  HIVE_HOME_URL,
  SSO_URL,
  SSO_CLIENT_ID,
  HRM_LINK,
  HIVE_GUIDELINE_LINK,
  INDIVIDUAL_POP_UP_MODE,
  LETTER_A_IN_ASCII_CODE,
  LOCAL_STORAGE_KEYS,
  MENU_ACTIONS,
  NOTIFICATION,
  PAGE_NAMES,
  PRIZE_TYPES,
  PROJECT_ASSESSMENT_STATUSES,
  PROJECT_ASSESSMENT_TOOLTIP,
  PROJECT_PEER_EVALUATION_STATUSES,
  PROJECT_ROLES,
  PROJECT_TYPES,
  REWARD_DROP,
  REWARD_PERIOD_STATUSES,
  REWARD_REQUEST_STATUSES,
  STATUS_COLORS,
  STATUS_TYPES,
  USER_ENGAGEMENT_ROLES,
  WELCOME_PROJECT_LIST,
  TASK_TYPES,
  HIVE_STATISTICS_TEXT,
  USER_ENGAGEMENT_SOURCES,
  USER_ENGAGEMENT_SOURCES_TEXT,
  TIME_TO_DETECT_AUTOMATICALLY_ADDED_PROJECTS,
  IMPORTED_USER_DATA
};

export const MANAGER_STATUS = {
  APPROVED: 'APPROVED',
  SAVED: 'SAVED'
};
